import { useState } from 'react';
import { Collapse, Grid, Typography, useTheme } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Cargo } from '../../../../../ts/Cargo';
import CustomFieldCard from './CustomFieldCard';
import ScrollableCardArea from '../ScrollableCardArea';
import { resolveCustomFieldAppearance } from './customFieldAppearance';
import { CustomField, CustomFieldDefinition } from '../../../../../ts/CustomFieldType';

interface CustomFieldsDetailsProps {
    cargoResponse: Cargo;
    /** Custom field definitions from the CustomFields API; may carry icon names and colors. */
    customFieldDefinitions?: CustomFieldDefinition[];
    fullWidth?: boolean;
    defaultOpen?: boolean;
}

export default function CustomFieldsDetails(props: CustomFieldsDetailsProps) {
    const { cargoResponse, customFieldDefinitions, fullWidth = false, defaultOpen = false } = props;
    const [open, setOpen] = useState<boolean>(defaultOpen);
    const { t } = useTranslation();
    const theme = useTheme();

    const fields: CustomField[] = _.has(cargoResponse, 'customFields.CustomFields.Field') && Array.isArray(cargoResponse.customFields.CustomFields.Field)
        ? cargoResponse.customFields.CustomFields.Field
        : [];

    const freightClass = cargoResponse?.dimensions?.freightClass;
    const hasFreightClass = Boolean(freightClass?.class);

    const freightCards = hasFreightClass
        ? [
            { label: t('control.eventHistory.scanDetails.freightClass'), value: freightClass?.class },
            { label: t('control.eventHistory.scanDetails.itemNumber'), value: freightClass?.itemNumber },
            { label: t('control.eventHistory.scanDetails.subItemNumber'), value: freightClass?.subItemNumber },
        ]
        : [];

    const hasContent = fields.length > 0 || freightCards.length > 0;

    return (
        <Grid
            container
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
            sx={{ mt: 1 }}
        >
            <Grid item md={fullWidth ? 12 : 11} xs={12}>
                <ListItemButton
                    sx={{
                        backgroundColor: fullWidth ? 'var(--spectre-surface-muted)' : 'var(--spectre-surface)',
                        borderRadius: 1,
                        color: theme.palette.spectre.textPrimary,
                        '&:hover': { backgroundColor: 'var(--spectre-input)' },
                    }}
                    onClick={() => setOpen(!open)}
                >
                    <ListItemText>
                        <Typography color="inherit" fontWeight={600}>{t('control.centerControl.moreDetails')}</Typography>
                    </ListItemText>
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    sx={{
                        backgroundColor: fullWidth ? 'var(--spectre-surface-muted)' : 'var(--spectre-page)',
                        borderBottomLeftRadius: 1,
                        borderBottomRightRadius: 1,
                    }}
                >
                    {hasContent ? (
                        <ScrollableCardArea visibleCards={3}>
                            <Grid container spacing={1.5} sx={{ pt: 1.5, pb: 1.5, width: 'calc(100% - 12px)', ml: 0 }}>
                                {fields.map((field: CustomField, index: number) => (
                                    <CustomFieldCard
                                        key={`custom-field-${field.Id ?? field.Name ?? index}`}
                                        label={field.Name ?? t('control.inputs.customFields.customFieldUnknown')}
                                        value={field.Value}
                                        appearance={resolveCustomFieldAppearance(field, theme, customFieldDefinitions)}
                                    />
                                ))}
                                {freightCards.map((card) => (
                                    <CustomFieldCard
                                        key={`freight-${card.label}`}
                                        label={card.label}
                                        value={card.value}
                                        appearance={{ color: theme.palette.spectre.fileAccentDefault }}
                                    />
                                ))}
                            </Grid>
                        </ScrollableCardArea>
                    ) : (
                        <Typography color="text.secondary" sx={{ px: 2, py: 2 }}>
                            {t('control.centerControl.customFieldDetails.empty')}
                        </Typography>
                    )}
                </Collapse>
            </Grid>
        </Grid>
    );
}
